<template>
  <transition-group name="notification-slide" tag="div" class="global-notifications">
    <div
      v-for="notification in notifications"
      :key="notification.id"
      class="global-notification"
      :class="'global-notification--' + notification.type"
    >
      <div class="global-notification__icon">
        <i :class="getIcon(notification.type)"></i>
      </div>
      <div class="global-notification__content">
        <p class="global-notification__message">{{ notification.message }}</p>
      </div>
      <button 
        class="global-notification__close"
        @click="removeNotification(notification.id)"
        aria-label="Fermer"
      >
        <i class="fas fa-times"></i>
      </button>
      <div 
        class="global-notification__progress"
        :style="{ animationDuration: notification.duration + 'ms' }"
      ></div>
    </div>
  </transition-group>
</template>

<script>
import NotificationService from '@/services/notification';

export default {
  name: 'GlobalNotifications',
  data() {
    return {
      notifications: [],
      nextId: 1,
      unsubscribe: null,
      timers: new Map()
    };
  },
  mounted() {
    // Subscribe to notification events
    this.unsubscribe = NotificationService.subscribe(this.addNotification);
  },
  beforeUnmount() {
    // Cleanup subscription
    if (this.unsubscribe) {
      this.unsubscribe();
    }
    // Clear all pending timers
    this.timers.forEach(timerId => clearTimeout(timerId));
    this.timers.clear();
  },
  methods: {
    addNotification({ message, type, duration }) {
      const id = this.nextId++;
      
      this.notifications.push({
        id,
        message,
        type,
        duration
      });

      // Auto-remove after duration (store timer reference for cleanup)
      const timerId = setTimeout(() => {
        this.removeNotification(id);
      }, duration);
      this.timers.set(id, timerId);
    },
    removeNotification(id) {
      const index = this.notifications.findIndex(n => n.id === id);
      if (index > -1) {
        this.notifications.splice(index, 1);
      }
      // Clear and remove timer reference
      if (this.timers.has(id)) {
        clearTimeout(this.timers.get(id));
        this.timers.delete(id);
      }
    },
    getIcon(type) {
      const icons = {
        success: 'fas fa-check-circle',
        error: 'fas fa-exclamation-circle',
        warning: 'fas fa-exclamation-triangle',
        info: 'fas fa-info-circle'
      };
      return icons[type] || icons.info;
    }
  }
};
</script>

<style scoped>
.global-notifications {
  position: fixed;
  top: 100px;
  right: 20px;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 400px;
  width: 100%;
  pointer-events: none;
}

.global-notification {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.2);
  position: relative;
  overflow: hidden;
  pointer-events: auto;
}

.global-notification__icon {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  flex-shrink: 0;
}

.global-notification__content {
  flex: 1;
  min-width: 0;
}

.global-notification__message {
  margin: 0;
  font-size: 0.9375rem;
  line-height: 1.5;
  color: #333;
  word-wrap: break-word;
}

.global-notification__close {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #999;
  font-size: 0.875rem;
  transition: color 0.2s;
  flex-shrink: 0;
}

.global-notification__close:hover {
  color: #333;
}

.global-notification__progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  width: 100%;
  animation: progress-shrink linear forwards;
}

@keyframes progress-shrink {
  from { width: 100%; }
  to { width: 0%; }
}

/* Type variants */
.global-notification--success {
  border-left: 4px solid var(--color-success);
}
.global-notification--success .global-notification__icon {
  color: var(--color-success);
}
.global-notification--success .global-notification__progress {
  background: var(--color-success);
}

.global-notification--error {
  border-left: 4px solid #E74C3C;
}
.global-notification--error .global-notification__icon {
  color: #E74C3C;
}
.global-notification--error .global-notification__progress {
  background: #E74C3C;
}

.global-notification--warning {
  border-left: 4px solid #F39C12;
}
.global-notification--warning .global-notification__icon {
  color: #F39C12;
}
.global-notification--warning .global-notification__progress {
  background: #F39C12;
}

.global-notification--info {
  border-left: 4px solid var(--color-primary);
}
.global-notification--info .global-notification__icon {
  color: var(--color-primary);
}
.global-notification--info .global-notification__progress {
  background: var(--color-primary);
}

/* Transition animations */
.notification-slide-enter-active,
.notification-slide-leave-active {
  transition: all 0.3s ease;
}

.notification-slide-enter,
.notification-slide-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

/* Mobile responsive */
@media (max-width: 480px) {
  .global-notifications {
    right: 10px;
    left: 10px;
    max-width: none;
    width: auto;
  }
}
</style>
